import React, { useState } from 'react';
import {
  CREATE_CHILD,
  DASHBOARD,
  LOGOUT,
  MY_ACCOUNT_PROFILE,
} from 'utilities/routes';

import { useNavigate } from 'react-router-dom';

import ButtonComponent from 'components/button/buttonComponent';

import { twMerge } from 'tailwind-merge';
import { AccountCard } from './AccountCard';
import { useAuth } from 'auth/context/AuthContext';

import { ReactComponent as Plus } from 'assets/icons/plusIcon.svg';
import { ReactComponent as Arrow } from 'assets/icons/upArrow.svg';
import { ReactComponent as LogoutIcon } from 'assets/icons/logout.svg';
import { useCustomToast } from 'layout/useToast';
import { interpolateVariables } from 'utilities/functions';
import { PATIENT_PROFILE_ROUTES } from '../../../app/my-account/pages/patient/constants';
import { usePatientProfileContext } from '../../../app/my-account/pages/patient/PatientProfileContext';
import { useIndicators } from '../../../context/UserIndicatorContext';

interface AccountDropdownProps {
  isOpen: boolean;
  locale: Record<string, Record<string, string>>;
}

const MAX_CHILD_ACCOUNTS = 10;

export const AccountDropdown: React.FC<AccountDropdownProps> = ({
  isOpen,
  locale,
}) => {
  const { selectedAccount } = useAuth();
  const navigate = useNavigate();

  const itemsClasses =
    'px-5 py-2 text-semibold flex items-center gap-2.5 text-btn-black hover:bg-hover-blue rounded-[5px]';

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
      }}
      className={`${
        !isOpen ? 'hidden' : 'flex'
      } w-[360px] flex-col gap-2.5 absolute top-full p-4 bg-white rounded-[20px] shadow-container z-[1001] right-0`}
    >
      <div className="pb-0">
        <Accounts locale={locale} />
      </div>

      <div>
        <div
          className={itemsClasses}
          onClick={() =>
            navigate(
              selectedAccount
                ? `${MY_ACCOUNT_PROFILE}/profile`
                : MY_ACCOUNT_PROFILE,
            )
          }
        >
          {locale?.myAccountOptions?.myProfile ?? 'My profile'}
        </div>
        <div
          className={itemsClasses}
          onClick={() =>
            navigate(
              `${MY_ACCOUNT_PROFILE}/${PATIENT_PROFILE_ROUTES.HEALTH_QUESTIONNAIRE}`,
            )
          }
        >
          {locale?.myAccountOptions?.healthQuestionnaire ??
            'Health questionnaire'}
        </div>

        <hr className="my-[5px]" />

        <div className={itemsClasses} onClick={() => navigate(LOGOUT)}>
          <LogoutIcon />
          {locale?.myAccountOptions?.logOut ?? 'Log out'}
        </div>
      </div>
    </div>
  );
};

interface AccountsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  locale: any;
}

export function Accounts({ locale }: AccountsProps) {
  return <AccountsBlock locale={locale} />;
}

export function AccountsBlock({ locale }: AccountsProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const { addToast } = useCustomToast();
  const navigate = useNavigate();
  const {
    user,
    accounts,
    handleSelectAccount,
    selectedAccount,
    setProfilePicture,
  } = useAuth();

  const { accountsIndicatorById } = useIndicators();

  const handleClickAccount = (accountId: string | null) => {
    if (!handleSelectAccount) return;

    const selectedAccount = accounts.find(
      (account) => account.id === accountId,
    );

    addToast({
      type: 'info-new',
      message: selectedAccount?.id
        ? interpolateVariables(locale.switchedToChildAccount, {
            name: selectedAccount?.name as string,
          })
        : (locale.switchedToAccount as string),
    });

    handleSelectAccount(accountId);
  };

  const handleAddChild = () => {
    return navigate(CREATE_CHILD);
  };

  const { userValidation, loading } = usePatientProfileContext();

  const disableCreateChildButton = !(
    userValidation.isQuestionnaireValid &&
    userValidation.isContactInfoValid &&
    userValidation.isPersonalInfoValid &&
    userValidation.isPersonaVerified &&
    !loading
  );

  const hideCreateChildButton =
    selectedAccount || accounts.length - 1 >= MAX_CHILD_ACCOUNTS;

  return (
    <div className="p-4 bg-base-content rounded-2xl w-full">
      <div
        className="font-semibold text-btn-black flex justify-between items-center"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        Accounts
        <Arrow
          className={twMerge('w-3', isExpanded ? 'rotate-0' : 'rotate-180')}
        />
      </div>

      <div
        className={`${
          isExpanded ? 'flex' : 'hidden'
        } flex-col gap-1.5 my-1.5 max-h-[300px] overflow-y-auto`}
      >
        {accounts.map((account) => (
          <AccountCard
            id={account.id || user?.uuid || ''}
            profilePicture={account.avatarImage}
            name={account.name}
            isChild={account.isChild}
            bgClasses="bg-white"
            showIndicator={
              accountsIndicatorById?.[(account.id || user?.uuid) as string]
                ?.inMail
            }
            borderClasses={`border ${
              selectedAccount === account.id
                ? 'border-clc-blue'
                : 'border-graph-border-color'
            }`}
            handleClickAccount={() => {
              navigate(DASHBOARD);
              handleClickAccount(account.id);
              setProfilePicture(account?.avatarImage || null);
            }}
          />
        ))}

        {!hideCreateChildButton && (
          <CreateChildAccountCard
            locale={locale}
            onClick={handleAddChild}
            disabled={disableCreateChildButton}
          />
        )}
      </div>
    </div>
  );
}

interface CreateChildAccountCardProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  locale: any;
  onClick: () => void;
  disabled?: boolean;
}

function CreateChildAccountCard({
  locale,
  onClick,
  disabled = false,
}: CreateChildAccountCardProps) {
  return (
    <div>
      <div
        onClick={() => {
          if (disabled) return;
          onClick();
        }}
        className={
          'w-full h-[64px] bg-white border border-graph-border-color border-dashed rounded-10 flex justify-center items-center p-2.5 cursor-pointer'
        }
      >
        <ButtonComponent
          testID="add-child-button"
          Icon={Plus}
          type="underline"
          iconPosition={'left'}
          containerClassName="gap-[5px]"
          disabled={disabled}
        >
          <span className="uppercase underline font-bold text-sm">
            {locale?.registerYourChild ?? 'Register your child'}
          </span>
        </ButtonComponent>
      </div>
    </div>
  );
}
